/* eslint-disable */
/**
 * DemoProvider — global fetch interceptor active only on /admin-demo.
 *
 * It hijacks window.fetch and serves canned JSON for any /api/* call,
 * letting all real Admin components run unmodified against a fake backend.
 *
 * Mutations (POST/PUT/DELETE) are simulated against the in-memory fixtures
 * and a toast informs the user the demo session won't persist.
 */
import { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import * as fx from '../data/demoFixtures';

const DemoContext = createContext(null);
export const useDemo = () => useContext(DemoContext);

const json = (body, status = 200) =>
  new Response(JSON.stringify(body), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });

const ok = () => json({ success: true });

let mutationToastQueued = false;
function notifyMutation(verb = 'saved') {
  if (mutationToastQueued) return;
  mutationToastQueued = true;
  setTimeout(() => { mutationToastQueued = false; }, 800);
  toast.info(`Demo mode — changes ${verb} for this session only`, {
    description: 'Refresh to reset the demo. Get the real thing with a Care Plan.',
    duration: 3000,
  });
}

const matches = (url, ...patterns) =>
  patterns.some(p => (typeof p === 'string' ? url.includes(p) : p.test(url)));

async function handleDemo(input, init = {}) {
  const url = typeof input === 'string' ? input : (input?.url || '');
  const method = (init.method || (typeof input === 'object' && input.method) || 'GET').toUpperCase();
  let body = null;
  if (init.body) {
    try { body = JSON.parse(init.body); } catch { body = init.body; }
  }

  // ─── AUTH ─────────────────────────────────────────────
  if (matches(url, '/api/admin/auth/me')) return json(fx.demoUser);
  if (matches(url, '/api/admin/auth/logout')) return ok();
  if (matches(url, '/api/auth/login')) {
    return json({ token: 'demo-token', ...fx.demoUser });
  }
  // Customer-side AuthProvider — there's no logged-in shopper in the demo.
  // Return 200 with null so the cart auth check is silent (no 401 noise).
  if (matches(url, '/api/auth/me')) return json(null);
  if (matches(url, '/api/auth/logout')) return ok();

  // ─── PUBLIC PRICING + SHOP ────────────────────────────
  if (matches(url, '/api/pricing') && !url.includes('/admin/')) {
    return json(fx.pricingState);
  }
  if (matches(url, '/api/shop/sale-banner') && !url.includes('/admin/')) {
    return json(fx.saleBanner);
  }
  if (matches(url, '/api/shop/settings') && !url.includes('/admin/')) {
    return json(fx.shopSettings);
  }
  if (matches(url, '/api/contact/settings') && !url.includes('/admin/')) {
    return json(fx.contactSettings);
  }
  if (matches(url, '/api/legal/') && !url.includes('/admin/')) {
    const slug = url.split('/api/legal/')[1].split('?')[0];
    return json(slug.includes('priv') ? fx.legal.privacy : fx.legal.terms);
  }

  // ─── ENQUIRIES (submissions) ──────────────────────────
  if (matches(url, '/api/admin/submissions')) {
    if (method === 'GET') return json(fx.enquiries);
    if (method === 'DELETE') {
      const id = url.split('/').pop();
      fx.enquiries.splice(0, fx.enquiries.length, ...fx.enquiries.filter(e => e.id !== id));
      notifyMutation('removed');
      return ok();
    }
  }

  // ─── ANALYTICS ────────────────────────────────────────
  if (matches(url, '/api/admin/analytics/dashboard')) {
    // Parse the `?days=N` param so 7/30/90 each give a fresh, scaled payload.
    const m = url.match(/[?&]days=(\d+)/);
    const days = m ? Number(m[1]) : 30;
    return json(fx.buildAnalytics(days));
  }
  if (matches(url, '/api/admin/analytics/settings')) {
    if (method === 'GET') return json(fx.analyticsSettings);
    notifyMutation();
    return json({ ...fx.analyticsSettings, ...(body || {}) });
  }
  if (matches(url, '/api/analytics/settings')) return json(fx.analyticsSettings);

  // ─── BLOG ─────────────────────────────────────────────
  if (matches(url, '/api/blog/posts')) return json(fx.blogPosts);
  if (matches(url, '/api/admin/blog')) {
    if (method === 'POST') {
      const post = { id: 'p-' + Date.now(), published_at: new Date().toISOString(), ...body };
      fx.blogPosts.unshift(post);
      notifyMutation('published');
      return json(post);
    }
    if (method === 'PUT') {
      const id = url.split('/').pop();
      const idx = fx.blogPosts.findIndex(p => p.id === id);
      if (idx >= 0) fx.blogPosts[idx] = { ...fx.blogPosts[idx], ...body };
      notifyMutation('updated');
      return json(fx.blogPosts[idx] || body);
    }
    if (method === 'DELETE') {
      const id = url.split('/').pop();
      fx.blogPosts.splice(0, fx.blogPosts.length, ...fx.blogPosts.filter(p => p.id !== id));
      notifyMutation('removed');
      return ok();
    }
  }

  // ─── PRICING (admin) ──────────────────────────────────
  if (matches(url, '/api/admin/pricing/reset-builder')) {
    notifyMutation('reset');
    return json(fx.pricingState);
  }
  if (matches(url, '/api/admin/pricing')) {
    if (method === 'GET') return json(fx.pricingState);
    if (method === 'PUT') {
      Object.assign(fx.pricingState, body || {});
      notifyMutation();
      return json(fx.pricingState);
    }
  }

  // ─── PRODUCTS / SHOP (admin) ──────────────────────────
  if (matches(url, '/api/admin/products/bulk-price-adjust') ||
      matches(url, '/api/admin/products/bulk-price-reset') ||
      matches(url, '/api/admin/products/bulk-price-round')) {
    notifyMutation('bulk-adjusted');
    return json({ updated: fx.products.length });
  }
  if (matches(url, '/api/admin/products/reorder')) { notifyMutation('reordered'); return ok(); }
  if (matches(url, '/api/admin/products')) {
    if (method === 'GET') return json(fx.products);
    if (method === 'POST') {
      const p = { id: 'prd-' + Date.now(), sales_30d: 0, status: 'active', sort: fx.products.length + 1, ...body };
      fx.products.push(p);
      notifyMutation('created');
      return json(p);
    }
    if (method === 'PUT') {
      const id = url.split('/').pop();
      const idx = fx.products.findIndex(p => p.id === id);
      if (idx >= 0) fx.products[idx] = { ...fx.products[idx], ...body };
      notifyMutation();
      return json(fx.products[idx] || body);
    }
    if (method === 'DELETE') {
      const id = url.split('/').pop();
      fx.products.splice(0, fx.products.length, ...fx.products.filter(p => p.id !== id));
      notifyMutation('removed');
      return ok();
    }
  }
  if (matches(url, '/api/admin/shop/sale-banner')) {
    if (method === 'GET') return json(fx.saleBanner);
    Object.assign(fx.saleBanner, body || {});
    notifyMutation();
    return json(fx.saleBanner);
  }
  if (matches(url, '/api/admin/shop/settings')) {
    if (method === 'GET') return json(fx.shopSettings);
    Object.assign(fx.shopSettings, body || {});
    notifyMutation();
    return json(fx.shopSettings);
  }
  if (matches(url, '/api/admin/upload')) {
    notifyMutation('uploaded');
    return json({ url: 'https://images.unsplash.com/photo-1558655146-d09347e92766?w=800' });
  }

  // ─── ORDERS ───────────────────────────────────────────
  if (matches(url, '/api/admin/orders')) {
    if (method === 'GET') {
      const archived = url.includes('archived=true');
      return json(fx.orders.filter(o => !!o.archived === archived));
    }
    if (url.endsWith('/archive')) {
      const id = url.split('/').slice(-2)[0];
      const o = fx.orders.find(x => x.id === id);
      if (o) o.archived = !o.archived;
      notifyMutation('archived');
      return ok();
    }
    if (url.endsWith('/status') || url.includes('/status')) {
      const id = url.split('/').slice(-2)[0];
      const o = fx.orders.find(x => x.id === id);
      if (o && body?.status) o.status = body.status;
      notifyMutation('updated');
      return ok();
    }
  }

  // ─── DISCOUNTS ────────────────────────────────────────
  if (matches(url, '/api/admin/discounts')) {
    if (method === 'GET') return json(fx.discounts);
    if (method === 'POST') {
      const d = { id: 'dc-' + Date.now(), uses: 0, active: true, ...body };
      fx.discounts.push(d);
      notifyMutation('created');
      return json(d);
    }
    if (url.endsWith('/toggle')) {
      const id = url.split('/').slice(-2)[0];
      const d = fx.discounts.find(x => x.id === id);
      if (d) d.active = !d.active;
      notifyMutation('toggled');
      return ok();
    }
    if (method === 'PUT') {
      const id = url.split('/').pop();
      const d = fx.discounts.find(x => x.id === id);
      if (d) Object.assign(d, body);
      notifyMutation();
      return json(d || body);
    }
    if (method === 'DELETE') {
      const id = url.split('/').pop();
      fx.discounts.splice(0, fx.discounts.length, ...fx.discounts.filter(d => d.id !== id));
      notifyMutation('removed');
      return ok();
    }
  }

  // ─── ACCOUNTS / RBAC ──────────────────────────────────
  if (matches(url, '/api/admin/accounts')) {
    if (method === 'GET') return json(fx.accounts);
    if (method === 'POST') {
      const a = { id: 'a-' + Date.now(), created_at: new Date().toISOString(), role: 'staff', permissions: [], ...body };
      fx.accounts.push(a);
      notifyMutation('invited');
      return json(a);
    }
    if (method === 'PUT') {
      const id = url.split('/').pop();
      const a = fx.accounts.find(x => x.id === id);
      if (a) Object.assign(a, body);
      notifyMutation();
      return json(a || body);
    }
    if (method === 'DELETE') {
      const id = url.split('/').pop();
      fx.accounts.splice(0, fx.accounts.length, ...fx.accounts.filter(a => a.id !== id));
      notifyMutation('removed');
      return ok();
    }
  }

  // ─── CONTACT FORM ─────────────────────────────────────
  if (matches(url, '/api/admin/contact/settings')) {
    if (method === 'GET') return json(fx.contactSettings);
    Object.assign(fx.contactSettings, body || {});
    notifyMutation();
    return json(fx.contactSettings);
  }

  // ─── LEGAL (admin) ────────────────────────────────────
  if (matches(url, '/api/admin/legal/')) {
    const slug = url.split('/api/admin/legal/')[1].split('?')[0];
    const target = slug.includes('priv') ? fx.legal.privacy : fx.legal.terms;
    if (method === 'GET') return json(target);
    Object.assign(target, body || {}, { last_updated: new Date().toISOString() });
    notifyMutation();
    return json(target);
  }

  // ─── FALLBACK — never let demo break ──────────────────
  console.warn('[demo-fetch] unhandled', method, url);
  return json({});
}

// Module-level state — survives StrictMode double-effects.
let _originalFetch = null;
let _patchedFetch = null;
let _installed = 0;

function installInterceptor() {
  if (_originalFetch === null) {
    _originalFetch = window.fetch.bind(window);
  }
  if (!_patchedFetch) {
    const API = process.env.REACT_APP_BACKEND_URL || '';
    _patchedFetch = async (input, init) => {
      const url = typeof input === 'string' ? input : input?.url || '';
      const isApiCall = url.startsWith(API + '/api/') || url.startsWith('/api/');
      if (!isApiCall) return _originalFetch(input, init);
      try {
        return await handleDemo(input, init);
      } catch (e) {
        console.error('[demo-fetch] error', e);
        return json({ error: 'demo error' }, 500);
      }
    };
  }
  // (Re)install — even after StrictMode cleanup may have restored.
  window.fetch = _patchedFetch;
  localStorage.setItem('admin_token', 'demo-token');
  _installed++;
}

function uninstallInterceptor() {
  _installed = Math.max(0, _installed - 1);
  if (_installed === 0 && _originalFetch) {
    window.fetch = _originalFetch;
    localStorage.removeItem('admin_token');
  }
}

export function DemoProvider({ children }) {
  // Synchronously patch before children render the first time. Critical so
  // AdminPage's useEffect sees the demo token + patched fetch.
  const [installedOnce] = useState(() => {
    installInterceptor();
    return true;
  });

  useEffect(() => {
    // StrictMode dev runs setup → cleanup → setup, which would otherwise
    // leave fetch unpatched after the first cleanup. Re-install on every setup.
    installInterceptor();
    return () => uninstallInterceptor();
  }, []);

  return <DemoContext.Provider value={{ demo: true, installedOnce }}>{children}</DemoContext.Provider>;
}
